// Phase 1 (iter1): 中央 ↔ Detail 境界の drag resize + localStorage 保存
import { useCallback, useEffect, useRef, useState } from 'react'

const STORAGE_KEY = 'cockpit_v2.detail_width'
const DEFAULT_WIDTH = 400
const MIN_WIDTH = 240

// Detail パネルの最大幅: viewport 依存。中央カラムに最低 320px を残す範囲で
// 1200px まで広げられる (旧 700px 固定上限を緩和)。
function maxWidth(): number {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1400
  return Math.max(MIN_WIDTH, Math.min(1200, vw - 320))
}

function readStored(): number {
  try {
    const v = parseInt(window.localStorage.getItem(STORAGE_KEY) ?? '', 10)
    return isNaN(v) ? DEFAULT_WIDTH : Math.min(maxWidth(), Math.max(MIN_WIDTH, v))
  } catch {
    return DEFAULT_WIDTH
  }
}

export function usePanelResize() {
  const [width, setWidth] = useState<number>(readStored)
  const dragging = useRef(false)
  const startX = useRef(0)
  const startWidth = useRef(0)

  const onDragHandleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    dragging.current = true
    startX.current = e.clientX
    startWidth.current = width
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }, [width])

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging.current) return
      // Detail は右にある、左ドラッグで幅を増やす
      const delta = startX.current - e.clientX
      const next = Math.min(maxWidth(), Math.max(MIN_WIDTH, startWidth.current + delta))
      setWidth(next)
    }
    const onMouseUp = () => {
      if (!dragging.current) return
      dragging.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      setWidth(w => {
        try { window.localStorage.setItem(STORAGE_KEY, String(w)) } catch { /* ignore */ }
        return w
      })
    }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [])

  const reset = useCallback(() => {
    setWidth(DEFAULT_WIDTH)
    try { window.localStorage.setItem(STORAGE_KEY, String(DEFAULT_WIDTH)) } catch { /* ignore */ }
  }, [])

  return { width, onDragHandleMouseDown, reset }
}
